import { useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { recountCountLine, rejectCountLine } from '../api/countSheets';
import type { ManagerCountLine } from '../types/countSheets';
import { countedText } from '../utils/countSheets';
import { Answers, FooterReason, Question } from './capture';
import { Modal } from './Modal';

// F4 · rechazar (con motivo) o pedir recontar un insumo contado. Ninguno de los dos cambia el inventario;
// recontar lo regresa a la lista de la misma persona.

const errorCode = (error: unknown) => (error instanceof ApiError ? (error.payload as { error?: string } | undefined)?.error : undefined);

export function CountLineReviewModal({ line, mode, onClose, onDone, onStale }: {
  line: ManagerCountLine;
  mode: 'reject' | 'recount';
  onClose: () => void;
  onDone: (message: string) => void;
  onStale: (message: string) => void;
}) {
  const [text, setText] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string>();
  const reject = mode === 'reject';
  const blocker = reject && text.trim().length < 5 ? 'Escribe por qué se rechaza (al menos 5 caracteres).' : null;
  const close = () => { if (!isBusy) onClose(); };

  const submit = async () => {
    if (isBusy || blocker) return;
    setIsBusy(true);
    setError(undefined);
    try {
      if (reject) await rejectCountLine(line.id, text.trim());
      else await recountCountLine(line.id, text.trim() || undefined);
      onDone(reject ? `Conteo de ${line.ingredient.name} rechazado. El inventario no cambió.` : `Se pidió recontar ${line.ingredient.name}: volvió a la lista de ${line.countedBy?.name ?? 'quien contó'}.`);
    } catch (reviewError) {
      const message = getErrorMessage(reviewError, reject ? 'No fue posible rechazar el conteo.' : 'No fue posible pedir el recuento.');
      if (errorCode(reviewError) === 'COUNT_SHEET_LINE_NOT_SUBMITTED' || errorCode(reviewError) === 'COUNT_SHEET_LINE_NOT_FOUND') { onStale(message); return; }
      setError(message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      title={reject ? `Rechazar conteo de ${line.ingredient.name}` : `Pedir recontar ${line.ingredient.name}`}
      description={countedText(line) ?? undefined}
      isBusy={isBusy}
      onClose={close}
      footer={<>
        <FooterReason>{blocker}</FooterReason>
        <button className="text-button" disabled={isBusy} onClick={close} type="button">Cancelar</button>
        <button className={`button ${reject ? 'button--danger' : 'button--primary'}`} disabled={isBusy || Boolean(blocker)} onClick={() => void submit()} type="button">{isBusy ? 'Guardando…' : reject ? 'Rechazar' : 'Pedir recontar'}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <Question>{reject ? '¿Por qué se rechaza?' : '¿Algo que deba saber al recontar? (opcional)'}</Question>
      <Answers>
        <label className="field">
          <span>{reject ? 'Motivo (lo verá quien contó)' : 'Nota para quien cuenta'}</span>
          <textarea maxLength={reject ? 300 : 200} onChange={(event) => { setText(event.target.value); setError(undefined); }} placeholder={reject ? 'Ej. Se contó en la tienda equivocada' : 'Ej. Revisa también las cajas de la bodega'} rows={2} value={text} />
        </label>
      </Answers>
      {/* Quien contó lee esta nota: si trae la cantidad del sistema, deja de contar a ciegas. */}
      <p className="wiz-hint">Quien contó verá esta nota: no escribas cuánto dice el sistema. {reject ? 'El inventario no cambia.' : 'El inventario no cambia hasta que apruebes el nuevo conteo.'}</p>
    </Modal>
  );
}
